import React from "react";
import styled from "styled-components";
import { Button, Tag } from "../../../../../design-system";
import mediaOps from "../../../../../database/mediaOps/mediaOpsApi";
import { useOpRead } from "./hooks";
import { Grow, Panel, PanelHead, ResultNotice, Row, Small, fmtTime } from "./ui";

type PostInsight = {
  publication: string;
  accountId: string;
  publishedAt: string | null;
  observedAt: string;
  source: string;
  values: Record<string, number | null>;
};

const Metrics = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 16px;
  margin: 20px 0;
  dt { color: var(--color-text-secondary); font-size: 12px; }
  dd { margin: 5px 0 0; font-size: 25px; font-weight: 600; font-variant-numeric: tabular-nums; }
`;

export const insightValue = (value: number | null | undefined): string =>
  typeof value === "number" && Number.isFinite(value) && value >= 0 ? value.toLocaleString() : "—";

const METRICS = [["views", "Views"], ["reach", "Accounts reached"], ["likes", "Likes"], ["comments", "Comments"], ["shares", "Shares"], ["saved", "Saves"]] as const;

export default function InsightsView({ communityId }: { communityId: string }) {
  const report = useOpRead(() => mediaOps.socialInsights(communityId), [communityId]);
  const accounts = useOpRead(() => mediaOps.listAccounts(communityId), [communityId]);
  const posts: PostInsight[] = report.result?.data?.posts || [];
  const names = new Map((accounts.result?.data?.accounts || []).map(a => [a.id, a.handle]));
  return <div>
    <Panel>
      <PanelHead>Post insights<Grow /><Button size="sm" variant="secondary" onClick={report.reload} isLoading={report.loading}>Refresh insights</Button></PanelHead>
      <Small>Latest observations collected in the last 28 days. Counts are cumulative for each post; accounts reached are unique accounts, not views.</Small>
      <Small>A dash means the platform has not supplied that metric. Zero means a measured zero.</Small>
      {report.result?.status === "blocked" ? <ResultNotice result={report.result} /> : null}
      {report.loading && !report.result ? <p>Loading post insights…</p> : null}
      {!report.loading && report.result?.status === "completed" && posts.length === 0 ? <p>No post insights collected yet. Open a published post in Deliveries and choose Refresh post metrics, then return here.</p> : null}
    </Panel>
    {posts.map(post => <Panel key={post.publication}>
      <Row><strong>{names.get(post.accountId) ? `@${String(names.get(post.accountId)).replace(/^@/, "")}` : "Connected account"}</strong><Grow /><Tag size="sm" variant={post.source === "provider" ? "success" : "warning"}>{post.source === "provider" ? "From platform" : "Manually recorded"}</Tag></Row>
      <Small>Published {post.publishedAt ? fmtTime(post.publishedAt) : "date unavailable"}</Small>
      <Metrics>{METRICS.map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{insightValue(post.values[key])}</dd></div>)}</Metrics>
      <Small>Last observed {fmtTime(post.observedAt)}</Small>
    </Panel>)}
  </div>;
}
